/**
 * One answer per key for the whole app, shared by every component that asks
 * (fastphone-2).
 *
 * The shell's facts (am I a platform admin, which plan, the get started list,
 * the onboarding status, the nav badges) were each kept in the asking
 * component's own state, so every component that used one asked the server
 * again: a cold open called `am_i_platform_admin` 6 times and `my_entitlements`
 * 4. A shared read keeps one answer per key, one call in flight per key, and
 * refreshes when its owner says so (`refresh`), never because a component
 * mounted.
 *
 * Plain module state on purpose, not the query client: these hooks are read
 * from places rendered outside a QueryClientProvider (tests, the side view).
 */
import { useEffect, useSyncExternalStore } from 'react';

export interface SharedSnapshot<T> {
    value: T | undefined;
    /** True until the first answer for this key has arrived. */
    loading: boolean;
}

interface Entry<T> {
    snap: SharedSnapshot<T>;
    inflight: Promise<void> | null;
    /** A refresh was asked for while a read was in flight. */
    again: boolean;
    /** Bumped by clear(), so an answer asked for before it is thrown away. */
    gen: number;
    listeners: Set<() => void>;
}

// Stable objects: useSyncExternalStore compares snapshots by reference.
const EMPTY: SharedSnapshot<never> = { value: undefined, loading: false };
const LOADING: SharedSnapshot<never> = { value: undefined, loading: true };

export interface SharedRead<T> {
    /** The hook: the current answer for `key` (null = nothing to ask). */
    use(key: string | null): SharedSnapshot<T>;
    /**
     * Ask again for one key, or (no key) for every key on screen now; an answer
     * nobody is reading is dropped and asked fresh on its next read. One call per key.
     */
    refresh(key?: string): void;
    /** Set the answer for a key without asking (an update the caller already knows). */
    set(key: string, value: T): void;
    /** Drop every answer (sign-out, tests). */
    clear(): void;
    /** Read the current answer outside React. */
    peek(key: string): T | undefined;
}

export function createSharedRead<T>(fetcher: (key: string) => Promise<T>): SharedRead<T> {
    const entries = new Map<string, Entry<T>>();

    const entry = (key: string): Entry<T> => {
        let e = entries.get(key);
        if (!e) {
            e = { snap: LOADING, inflight: null, again: false, gen: 0, listeners: new Set() };
            entries.set(key, e);
        }
        return e;
    };
    const publish = (e: Entry<T>, snap: SharedSnapshot<T>) => {
        e.snap = snap;
        e.listeners.forEach((l) => l());
    };
    const load = (key: string): void => {
        const e = entry(key);
        if (e.inflight) { e.again = true; return; }
        const gen = e.gen;
        const read: Promise<void> = fetcher(key)
            .then((value) => { if (gen === e.gen) publish(e, { value, loading: false }); })
            // A failed read keeps the last answer (or settles to none), never spins.
            .catch(() => { if (gen === e.gen) publish(e, { value: e.snap.value, loading: false }); })
            .finally(() => {
                if (e.inflight !== read) return; // a clear() started a newer read
                e.inflight = null;
                if (e.again) { e.again = false; load(key); }
            });
        e.inflight = read;
    };
    const started = (key: string) => {
        const e = entries.get(key);
        return !!e && (e.inflight !== null || !e.snap.loading);
    };

    return {
        use(key) {
            const snap = useSyncExternalStore(
                (onChange) => {
                    if (!key) return () => {};
                    const e = entry(key);
                    e.listeners.add(onChange);
                    return () => { e.listeners.delete(onChange); };
                },
                () => (key ? entries.get(key)?.snap ?? LOADING : EMPTY),
            );
            useEffect(() => {
                if (key && !started(key)) load(key);
            }, [key]);
            return snap as SharedSnapshot<T>;
        },
        refresh(key) {
            if (key) { if (entries.has(key)) load(key); return; }
            for (const [k, e] of entries) {
                if (e.listeners.size) load(k);
                else if (!e.inflight) entries.delete(k);
            }
        },
        set(key, value) {
            publish(entry(key), { value, loading: false });
        },
        clear() {
            for (const [key, e] of entries) {
                e.gen += 1;
                e.again = false;
                publish(e, LOADING);
                // Someone is still reading this key: ask again for them.
                if (e.listeners.size) { e.inflight = null; load(key); } else entries.delete(key);
            }
        },
        peek(key) {
            return entries.get(key)?.snap.value;
        },
    };
}
